import { useCallback, useEffect, useRef, useState } from 'react'
import { BackHandler } from 'react-native'

import { createNavigationContainerRef, NavigationAction, NavigationState, PartialState } from '@react-navigation/native'

import { extendLogger, LoggerContexts } from '../utils/logger'
// import { DrawerNavigatorParamList, DrawerRoutes } from './drawer-navigator'
// import { HomeStackParams } from './homeNavigator'
// import { RootParamList } from './root-navigator'
// import { ModalParamList } from './game-navigator'
// import { TabRoutes } from './tab-routes'
import { LastVisitedTab } from '@/models/session/session-user'
import { Route } from 'expo-router'

const logger = extendLogger(LoggerContexts.NavigationUtilities)

interface StorageStatic {
  loadString: (key: string) => Promise<string | null>
  saveString: (key: string, value: string) => Promise<void>
  load: (key: string) => Promise<unknown | null>
  save: (key: string, value: unknown) => Promise<void>
  remove: (key: string) => void
  clear: () => void
}
/* eslint-disable */
export const RootNavigation = {
  navigate(_name: string, _params?: any) {},
  goBack() {},
  resetRoot(_state?: PartialState<NavigationState> | NavigationState) {},
  getRootState(): NavigationState {
    return {} as any
  },
  dispatch(_action: NavigationAction) {}
}
/* eslint-enable */

export const navigationRef = createNavigationContainerRef()

/**
 * Gets the current screen from any navigation state.
 */
export function getActiveRouteName(state: NavigationState | PartialState<NavigationState>): string {
  if (state?.index !== undefined) {
    const route = state.routes[state.index]

    // Found the active route -- return the name
    if (!route.state) return route.name

    // Recursive call to deal with nested routers
    return getActiveRouteName(route.state)
  }
  return ''
}

/**
 * Gets the current route from any navigation state.
 */
export function getActiveRoute(state: NavigationState | PartialState<NavigationState>) {
  if (state?.index !== undefined) {
    const route = state.routes[state.index]

    // Found the active route -- return the name
    if (!route.state) return route

    // Recursive call to deal with nested routers
    return getActiveRoute(route.state)
  }
  return
}

/**
 * Hook that handles Android back button presses and forwards those on to
 * the navigation or allows exiting the app.
 */
export function useBackButtonHandler(canExit: (routeName: string) => boolean) {
  const canExitRef = useRef(canExit)

  useEffect(() => {
    canExitRef.current = canExit
  }, [canExit])

  useEffect(() => {
    // We'll fire this when the back button is pressed on Android.
    const onBackPress = () => {
      if (!navigationRef.isReady()) {
        return false
      }

      // grab the current route
      const routeName = getActiveRouteName(navigationRef.getRootState())

      // are we allowed to exit?
      if (canExitRef.current(routeName)) {
        // exit and let the system know we've handled the event
        BackHandler.exitApp()
        return true
      }

      // we can't exit, so let's turn this into a back action
      if (navigationRef.canGoBack()) {
        navigationRef.goBack()
        return true
      }

      return false
    }

    // Subscribe when we come to life
    const listener = BackHandler.addEventListener('hardwareBackPress', onBackPress)

    // Unsubscribe when we're done
    return () => listener.remove()
  }, [])
}

/**
 * Custom hook for persisting navigation state.
 */
export function useNavigationPersistence(storage: StorageStatic, persistenceKey: string) {
  //Todo: R&D for type of initial navigation state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [initialNavigationState, setInitialNavigationState] = useState<any>()

  // This feature is particularly useful in development mode.
  // It is selectively enabled in development mode with
  // the following approach. If you'd like to use navigation persistence
  // in production, remove the __DEV__ and set the state to false
  // DEV ONLY:
  // const [isRestored, setIsRestored] = useState(!__DEV__)
  // PRODUCTION:
  const [isRestored, setIsRestored] = useState(false)

  const routeNameRef = useRef<string | undefined>(undefined)

  const onNavigationStateChange = (state?: NavigationState) => {
    // logger.log('[useNavigationPersistence.onNavigationStateChange]', state)
    const previousRouteName = routeNameRef.current
    const currentRouteName = state ? getActiveRouteName(state) : ''

    if (previousRouteName !== currentRouteName) {
      // track screens.
      // __DEV__ && logger.tron.log(currentRouteName)
      logger.debug('currentRouteName', currentRouteName)
    }

    // Save the current route name for later comparision
    routeNameRef.current = currentRouteName

    // Persist state to storage
    storage.save(persistenceKey, state)
  }

  const restoreState = useCallback(async () => {
    try {
      const state = await storage.load(persistenceKey)
      //NOTE: storage.load is defined as (unknown | null) in storage.ts
      if (state) setInitialNavigationState(state)
    } finally {
      setIsRestored(true)
    }
  }, [storage, persistenceKey])

  useEffect(() => {
    if (!isRestored) restoreState()
  }, [isRestored, restoreState])

  return {
    onNavigationStateChange,
    restoreState,
    isRestored,
    initialNavigationState
  }
}

/**
 * use this to navigate to navigate without the navigation
 * prop. If you have access to the navigation prop, do not use this.
 * More info: https://reactnavigation.org/docs/navigating-without-navigation-prop/
 */
export function navigate(name: string, params?: NavigationState) {
  if (navigationRef.isReady()) {
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigationRef.navigate(name as any, params as any)
  }
}

export function goBack() {
  if (navigationRef.isReady() && navigationRef.canGoBack()) {
    navigationRef.goBack()
  }
}

export function resetRoot(params = { index: 0, routes: [] }) {
  if (navigationRef.isReady()) {
    navigationRef.resetRoot(params)
  }
}

export function getCurrentRouteName() {
  if (navigationRef.isReady()) {
    return getActiveRouteName(navigationRef.getRootState())
  }
}

export const getRouteToLastVisitedTab = (lastPage?: LastVisitedTab): Route => {
  switch (lastPage) {
    case 'play':
      return '/(root)/(tabs)/play'
    case 'learning':
      return '/(root)/(tabs)/learning'
    case 'home':
      return '/(root)/(tabs)/home'
    default:
      return '/(root)/(tabs)'
  }
}
